import { Shield, Activity, AlertTriangle, CheckCircle, Lock, ShieldCheck, Ban, FileCheck, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SecurityOverview, SecuritySummary } from '../../services/dashboardSummary.service';
import { calculatePostureScore, type ThreatLevel } from '../../utils/analytics';

interface SecurityPostureCardProps {
    data: SecurityOverview | null;
    loading: boolean;
    aclStats?: SecuritySummary | null;
}

const getThreatLevelConfig = (level: ThreatLevel | 'Unknown') => {
    switch (level) {
        case 'Unknown':
            // No traffic inspected yet — neutral, not a reassuring green.
            return {
                color: 'text-gray-500',
                bgColor: 'bg-gray-100',
                borderColor: 'border-gray-300',
                icon: HelpCircle,
            };
        case 'Critical':
            return {
                color: 'text-danger-600',
                bgColor: 'bg-danger-100',
                borderColor: 'border-danger-500',
                icon: AlertTriangle,
            };
        case 'High':
            return {
                color: 'text-warning-600',
                bgColor: 'bg-warning-100',
                borderColor: 'border-warning-500',
                icon: AlertTriangle,
            };
        case 'Medium':
            return {
                color: 'text-warning-500',
                bgColor: 'bg-warning-50',
                borderColor: 'border-warning-400',
                icon: Activity,
            };
        case 'Low':
        default:
            return {
                color: 'text-success-600',
                bgColor: 'bg-success-100',
                borderColor: 'border-success-500',
                icon: CheckCircle,
            };
    }
};

const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(0);
};

export const SecurityPostureCard = ({ data, loading, aclStats }: SecurityPostureCardProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
            </div>
        );
    }

    if (!data || data.totalEdges === 0) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Security Posture</h2>
                    <Link
                        to="/monitoring/suricata"
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                        View Details →
                    </Link>
                </div>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Shield className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No edge devices available</p>
                    <p className="text-xs text-gray-400 mt-1">Deploy edges to view security posture</p>
                </div>
            </div>
        );
    }

    // Block rate — and therefore the threat level — is only meaningful once
    // traffic has actually been inspected. With nothing inspected, "Low Threat"
    // would be a reassuring green lie that contradicts a 0/100 posture; show a
    // neutral "No Threat Data" state instead.
    const hasThreatVisibility = data.packetsInspected > 0;
    const config = getThreatLevelConfig(hasThreatVisibility ? data.threatLevel : 'Unknown');
    const ThreatIcon = config.icon;
    const threatLabel = hasThreatVisibility ? `${data.threatLevel} Threat` : 'No Threat Data';

    // Calculate compliance percentage (edges with ACL enabled / total)
    const compliancePercent = aclStats
        ? Math.round((aclStats.aclEnabled / Math.max(data.totalEdges, 1)) * 100)
        : Math.round((data.edgesWithSecurity / data.totalEdges) * 100);

    // Posture score (coverage-based, higher = better) — the headline number,
    // distinct from the threat badge. Uses the same "edges protected" figure
    // shown in the tile below so the headline and the tile can't disagree.
    const edgesProtected = aclStats?.aclEnabled ?? data.edgesWithSecurity;
    const postureScore = calculatePostureScore({
        edgesProtected,
        edgesWithPolicies: aclStats?.edgesWithPolicies ?? data.edgesWithPolicies,
        totalEdges: data.totalEdges,
    });
    const postureColor = postureScore >= 90 ? 'text-success-600'
        : postureScore >= 70 ? 'text-warning-600' : 'text-danger-600';

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Security Posture</h2>
                <Link
                    to="/monitoring/acl"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                    View Details →
                </Link>
            </div>

            {/* Threat Level Badge */}
            <div className="flex items-center space-x-4 mb-4">
                <div
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 ${config.bgColor} ${config.borderColor}`}
                >
                    <ThreatIcon className={`w-5 h-5 ${config.color}`} />
                    <span className={`text-lg font-bold ${config.color}`}>{threatLabel}</span>
                </div>
                <div
                    className="text-sm text-gray-600"
                    title="Security posture: higher is better. Reflects how many edges have security enabled and policies configured. The threat level (left) is a separate axis."
                >
                    Posture: <span className={`font-semibold ${postureColor}`}>{postureScore}/100</span>
                </div>
            </div>

            {/* Policy Compliance Bar */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                        <ShieldCheck className="w-4 h-4 text-success-500" />
                        <span className="text-sm font-medium text-gray-700">Policy Compliance</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{compliancePercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className={`h-2 rounded-full transition-all ${compliancePercent >= 90 ? 'bg-success-500' :
                                compliancePercent >= 70 ? 'bg-warning-500' : 'bg-danger-500'
                            }`}
                        style={{ width: `${compliancePercent}%` }}
                    />
                </div>
            </div>

            {/* Stats Grid - 2x2 or 2x3 if ACL stats available */}
            <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-1 text-xs text-gray-500 uppercase tracking-wide">
                        <Lock className="w-3 h-3" />
                        <span>Edges Protected</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                        {edgesProtected}/{data.totalEdges}
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-1 text-xs text-gray-500 uppercase tracking-wide">
                        <Ban className="w-3 h-3" />
                        <span>Blocked</span>
                    </div>
                    <div className="text-xl font-bold text-danger-600 mt-1">
                        {formatNumber(aclStats?.packetsBlocked ?? data.packetsBlocked)}
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-1 text-xs text-gray-500 uppercase tracking-wide">
                        <CheckCircle className="w-3 h-3" />
                        <span>Permitted</span>
                    </div>
                    <div className="text-xl font-bold text-success-600 mt-1">
                        {formatNumber(aclStats?.packetsPermitted ?? (data.packetsInspected - data.packetsBlocked))}
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-1 text-xs text-gray-500 uppercase tracking-wide">
                        <FileCheck className="w-3 h-3" />
                        <span>Active Rules</span>
                    </div>
                    <div className="text-xl font-bold text-gray-900 mt-1">
                        {formatNumber(aclStats?.totalPolicies ?? data.activePolicies)}
                    </div>
                </div>
            </div>

            {/* Block Rate Summary */}
            <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-gray-500">Block Rate</span>
                <span className={`font-semibold ${data.blockRatePercent > 5 ? 'text-danger-600' :
                        data.blockRatePercent > 1 ? 'text-warning-600' : 'text-success-600'
                    }`}>
                    {(aclStats?.blockRate ?? data.blockRatePercent).toFixed(2)}%
                </span>
            </div>
        </div>
    );
};

export default SecurityPostureCard;
